import React, { CSSProperties } from 'react';
import { Point } from '../coreTypes';
import { clamp, throttle } from '../coreUtils';
import ZoomableContainerStyled from './ZoomableContainer.styles';

export interface IZoomableContainerProps {
  zoomProps?: {
    value?: number;
    min?: number;
    max?: number;
    step?: number;
  }
  anchor?: {
    horizontal: number; // 0 -> left, 1 -> right, 0.5 -> center
    vertical: number;   // 0 -> top, 1 -> bottom, 0.5 -> center
  }
  style?: CSSProperties;
  id?: string;

  onChange?: (newZoom: number) => void;
  onScrollChange?: (newOffset: Point) => void;
}

export interface IZoomableContainerState {
  grabbing: boolean;
}

export class ZoomableContainer extends React.Component<IZoomableContainerProps, IZoomableContainerState> {
  private containerRef = React.createRef<HTMLDivElement>();
  private positionerRef = React.createRef<HTMLDivElement>();
  private zoomableRef = React.createRef<HTMLDivElement>();
  
  // needed for content drag
  private prevMousePos = { x: 0, y: 0 };

  constructor(props: IZoomableContainerProps) {
    super(props);

    this.state = {
      grabbing: false
    }
  }

  private withRefs = <T extends any>(callback: (refs: { container: HTMLDivElement, positioner: HTMLDivElement, zoomable: HTMLDivElement }) => T) => {
    const container = this.containerRef.current;
    const positioner = this.positionerRef.current;
    const zoomable = this.zoomableRef.current;
    if (container && positioner && zoomable)
      return callback({ container, positioner, zoomable });
    return undefined;
  }

  public getFittingZoom = () => this.withRefs(({ container, zoomable }) => {
    const factX = container.offsetWidth / zoomable.offsetWidth;
    const factY = container.offsetHeight / zoomable.offsetHeight;
    return Math.log(Math.min(factX, factY));
  }) || 0;

  public getZoom = (props?: IZoomableContainerProps) => {
    props = props || this.props;
    return props.zoomProps?.value === undefined ? this.getFittingZoom() : props.zoomProps.value;
  };

  // cache zoom pos for consistent quick zooming
  private zoomCache?: { time: number; containerPoint: Point; zoomablePoint: Point;}
  private chacheDuration = 1000; // time before cache invalidates
  
  private checkZoomCache = (containerPoint: Point) => {
    if (this.zoomCache) {
      if (this.zoomCache.time + this.chacheDuration > Date.now()) {
        return Math.abs(containerPoint.x - this.zoomCache.containerPoint.x) < 2
            && Math.abs(containerPoint.y - this.zoomCache.containerPoint.y) < 2;
      }
    }
    return false;
  };


  // extra for scroll positions, since react doesn't handle that
  private zoomRef: {
    containerPoint: Point;
    zoomablePoint: Point;
  } | null = null;

  public zoomToPoint = throttle((newZoomVal: number, clientPoint: Point) => {
    const onChange = this.props.onChange;
    if (onChange === undefined)
      return;
    
    this.withRefs(({ container }) => {
      const containerRect = container.getBoundingClientRect();
      const containerScaleX = container.offsetWidth / containerRect.width;
      const containerScaleY = container.offsetHeight / containerRect.height;
      const containerPoint = { x: (clientPoint.x - containerRect.left) * containerScaleX, y: (clientPoint.y - containerRect.top) * containerScaleY };
        
      const zoomablePoint = this.getZPFromCP(containerPoint);
      this.zoomRef = { containerPoint, zoomablePoint };
    });
    onChange(clamp(newZoomVal, this.props.zoomProps?.min, this.props.zoomProps?.max));
  }, 1000/60);

  private handleScroll = ((event: WheelEvent) => {
    if (event.ctrlKey) {
      event.stopPropagation();
      event.preventDefault();
      
      const oldValue = this.getZoom();
      const newZoomVal = oldValue + Math.sign(event.deltaY) * -(this.props.zoomProps?.step || 0.1);
      this.zoomToPoint(newZoomVal, { x: event.clientX, y: event.clientY });
    }
  });

  private scrollChange = () => this.withRefs(({ container }) => {
    if (this.props.onScrollChange)
      this.props.onScrollChange({ x: container.scrollLeft, y: container.scrollTop });
  });

  private handleMouseMove = (event: MouseEvent) => {
    if (this.state.grabbing) {
      const newPos = { x: event.clientX, y: event.clientY };
      const delta = { x: this.prevMousePos.x - newPos.x, y: this.prevMousePos.y - newPos.y };
      this.withRefs(({ container }) => {
        container.scrollLeft += delta.x;
        container.scrollTop += delta.y;
      });
      this.prevMousePos = newPos;
    }
  };

  private handleMouseDown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (event.button === 2 && this.props.zoomProps?.value !== undefined) {
      this.prevMousePos = { x: event.clientX, y: event.clientY };
      this.setState({
        grabbing: true
      });
    }
  };

  private handleMouseUp = (event: MouseEvent) => {
    if (event.button === 2) {
      this.setState({
        grabbing: false
      });
    }
  };

  private getZPFromCP = (containerPoint: Point, props?: IZoomableContainerProps) => {
    const oldZoom = Math.exp(this.getZoom(props));
    if (this.checkZoomCache(containerPoint))
      return this.zoomCache!.zoomablePoint;
    return this.withRefs(({ container, positioner }) => ({
      x: (containerPoint.x + container.scrollLeft - positioner.offsetLeft) / oldZoom,
      y: (containerPoint.y + container.scrollTop - positioner.offsetTop) / oldZoom
    })) || { x: 0, y: 0 };
  };

  getSnapshotBeforeUpdate = (prevProps: IZoomableContainerProps) => {
    if (prevProps.zoomProps?.value !== this.props.zoomProps?.value && !this.zoomRef) {
      const containerPoint = this.withRefs(({ container }) => ({
        x: container.offsetWidth * (this.props.anchor ? this.props.anchor.horizontal : 0.5),
        y: container.offsetHeight * (this.props.anchor ? this.props.anchor.vertical : 0.5)
      })) || { x: 0, y: 0 };
      const zoomablePoint = this.getZPFromCP(containerPoint, prevProps);
      this.zoomRef = { containerPoint, zoomablePoint };
    }
    return this.zoomRef;
  };

  componentDidUpdate = () => {
    const zoomRef = this.zoomRef;
    if (zoomRef) {
      const newZoom = Math.exp(this.getZoom());
      this.withRefs(({ container }) => {
        container.scrollLeft = zoomRef.zoomablePoint.x * newZoom - zoomRef.containerPoint.x;
        container.scrollTop = zoomRef.zoomablePoint.y * newZoom - zoomRef.containerPoint.y;
      });
      this.zoomCache = { time: Date.now(), ...zoomRef };
      this.zoomRef = null;
    }
  };

  public scrollTo = (point: Point, smooth = false) => this.withRefs(
    ({ container }) => container.scrollTo({ top: point.y, left: point.x, behavior: smooth ? 'smooth' : undefined })
  );

  public scrollBy = (delta: Point, smooth = false) => this.withRefs(
    ({ container }) => container.scrollBy({ top: delta.y, left: delta.x, behavior: smooth ? 'smooth' : undefined })
  );

  public getScroll = () => this.withRefs(
    ({ container }) => ({ x: container.scrollLeft, y: container.scrollTop })
  ) || { x: 0, y: 0 };

  private resize = () => {
    if (!this.props.zoomProps?.value) {
      this.forceUpdate();
    }
  }

  public componentDidMount() {
    this.containerRef.current?.addEventListener('wheel', this.handleScroll, { passive: false });
    window.addEventListener('resize', this.resize);
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  }

  public componentWillUnmount() {
    this.containerRef.current?.removeEventListener('wheel', this.handleScroll);
    window.removeEventListener('resize', this.resize);
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  }

  public render() {
    const zoom = Math.exp(this.getZoom());
    const zoomable = this.zoomableRef.current;
    return (
      <ZoomableContainerStyled ref={this.containerRef} className='ZoomableContainer'
        onScroll={this.scrollChange}
        style={{
          ...this.props.style,
          cursor: this.props.style?.cursor || (this.state.grabbing ? 'grabbing' : undefined),
          overflow: this.props.style?.overflow || (this.props.zoomProps?.value === undefined ? 'hidden' : 'auto')
        }}
      >
        <div ref={this.positionerRef}
          className='ZoomableContainerPosition'
          style={zoomable ? {
            width: zoomable.offsetWidth * zoom, height: zoomable.offsetHeight * zoom
          } : { width: 0, height: 0}}>
          <div ref={this.zoomableRef}
            onMouseDown={this.handleMouseDown}
            className='ZoomableContainerZoom'
            style={{ transform: `scale(${zoom})` }}
          >
            {this.props.children}
          </div>
        </div>
      </ZoomableContainerStyled>
    );
  }
}
